import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, Plus, Search, ArrowLeft, Download, Eye, Trash2, Edit2,
  File, FileSpreadsheet, FileImage, X, Upload, Calendar
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { regulationsAPI } from '../services/api';

interface Regulation {
  id: string;
  title: string;
  description: string;
  category: 'reglamento' | 'acta' | 'manual' | 'otros';
  fileUrl: string;
  fileName: string;
  fileSize: string;
  fileType: string;
  uploadedAt: string;
  uploadedBy: string;
  downloadCount: number;
}

// Mock data removed — page fetches real data from regulationsAPI

export default function Reglamentos() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [regulations, setRegulations] = useState<Regulation[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | Regulation['category']>('all');
  const [showNewModal, setShowNewModal] = useState(false);
  const [viewingRegulation, setViewingRegulation] = useState<Regulation | null>(null);

  const { isAdmin } = useAuth();

  useEffect(() => {
    loadRegulations();
  }, []);

  const loadRegulations = async () => {
    setLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const condoId = userData.condoId;
      const response = await regulationsAPI.getAll(condoId);
      const data = response.data || response || [];
      setRegulations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading regulations:', err);
      setRegulations([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredRegulations = regulations.filter(r => {
    if (filter !== 'all' && r.category !== filter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        r.title.toLowerCase().includes(query) ||
        r.description.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const handleDownload = (regulation: Regulation) => {
    setRegulations(prev => prev.map(r => 
      r.id === regulation.id ? { ...r, downloadCount: r.downloadCount + 1 } : r
    ));
    alert(`📥 Descargando: ${regulation.fileName}`);
  };

  const handleDelete = (id: string) => {
    if (confirm('¿Eliminar este documento?')) {
      setRegulations(prev => prev.filter(r => r.id !== id));
    }
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      reglamento: 'Reglamento',
      acta: 'Acta',
      manual: 'Manual',
      otros: 'Otros'
    };
    return labels[category] || category;
  };

  const getFileIcon = (fileType: string) => {
    switch (fileType.toLowerCase()) {
      case 'pdf':
        return <FileText className="w-8 h-8 text-red-500" />;
      case 'xlsx':
      case 'xls':
        return <FileSpreadsheet className="w-8 h-8 text-emerald-500" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
        return <FileImage className="w-8 h-8 text-purple-500" />;
      default:
        return <File className="w-8 h-8 text-gray-500" />;
    }
  };

  const getCategoryBadgeColor = (category: string) => {
    switch (category) {
      case 'reglamento': return 'bg-blue-100 text-blue-700';
      case 'acta': return 'bg-purple-100 text-purple-700';
      case 'manual': return 'bg-amber-100 text-amber-700';
      default: return 'bg-[var(--v-surface-2)] text-gray-700';
    }
  };

  return (
    <div className="min-h-full bg-[var(--v-base-bg)]" style={{ fontFamily: 'Inter, sans-serif' }}>
      <header className="sticky top-0 bg-[var(--v-surface-1)] border-b border-[var(--v-border-light)] z-50">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/home/dashboard')} className="p-2 text-[#2D3748] hover:bg-[var(--v-surface-2)] rounded-xl">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[#2D3748]">Reglamentos y Documentos</h1>
              <p className="text-xs text-gray-500">Normativa y documentación del condominio</p>
            </div>
          </div>
          {isAdmin() && (
            <button 
              onClick={() => setShowNewModal(true)}
              className="px-4 py-2 bg-[#36CCD8] text-white font-bold rounded-xl hover:bg-[#2DB8C4] flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Subir Documento
            </button>
          )}
        </div>
      </header>

      <main className="p-6 max-w-4xl mx-auto">
        {/* Featured Document — only show if there's at least one regulation */}
        {filteredRegulations.length > 0 && (() => {
          const featured = filteredRegulations.find(r => r.category === 'reglamento') || filteredRegulations[0];
          return (
            <div className="bg-gradient-to-r from-[#36CCD8] to-[#2DB8C4] rounded-2xl p-6 mb-6 text-white">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                  <FileText className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold mb-2">{featured.title}</h2>
                  <p className="text-white/90 mb-4">{featured.description}</p>
                  <div className="flex items-center gap-4">
                    <button 
                      onClick={() => handleDownload(featured)}
                      className="px-4 py-2 bg-[var(--v-surface-1)] text-[#36CCD8] font-bold rounded-xl hover:bg-white/90 flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" /> Descargar PDF
                    </button>
                    <span className="text-sm text-white/80">{featured.downloadCount} descargas</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Filters */}
        <div className="bg-[var(--v-surface-1)] rounded-2xl border border-[var(--v-border-light)] shadow-sm p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar documentos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl focus:border-[#36CCD8] focus:outline-none"
              />
            </div>
            <div className="flex gap-2">
              {(['all', 'reglamento', 'acta', 'manual', 'otros'] as const).map((cat) => (
                <button
                  key={cat}
                  onClick={() => setFilter(cat)}
                  className={`px-4 py-2 rounded-xl font-medium text-sm transition-all ${
                    filter === cat
                      ? 'bg-[#36CCD8] text-white'
                      : 'bg-[var(--v-surface-2)] text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {cat === 'all' ? 'Todos' : getCategoryLabel(cat)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Documents List */}
        <div className="space-y-4">
          {filteredRegulations.map(regulation => (
            <div key={regulation.id} className="bg-[var(--v-surface-1)] rounded-2xl border border-[var(--v-border-light)] shadow-sm p-5 hover:shadow-md transition-all">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-[var(--v-surface-2)] rounded-xl flex items-center justify-center flex-shrink-0">
                  {getFileIcon(regulation.fileType)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-[#2D3748]">{regulation.title}</h3>
                    <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${getCategoryBadgeColor(regulation.category)}`}>
                      {getCategoryLabel(regulation.category)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{regulation.description}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-400">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(regulation.uploadedAt).toLocaleDateString('es-MX')}</span>
                    <span>{regulation.fileSize}</span>
                    <span>{regulation.downloadCount} descargas</span>
                    <span>Por: {regulation.uploadedBy}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => setViewingRegulation(regulation)}
                    className="p-2 text-gray-400 hover:text-[#36CCD8] hover:bg-[#36CCD8]/10 rounded-xl"
                    title="Ver"
                  >
                    <Eye className="w-5 h-5" />
                  </button>
                  <button 
                    onClick={() => handleDownload(regulation)}
                    className="p-2 text-gray-400 hover:text-[#36CCD8] hover:bg-[#36CCD8]/10 rounded-xl"
                    title="Descargar"
                  >
                    <Download className="w-5 h-5" />
                  </button>
                  {isAdmin() && (
                    <>
                      <button 
                        className="p-2 text-gray-400 hover:text-[#36CCD8] hover:bg-[#36CCD8]/10 rounded-xl"
                        title="Editar"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button 
                        onClick={() => handleDelete(regulation.id)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl"
                        title="Eliminar"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
          {filteredRegulations.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No se encontraron documentos</p>
            </div>
          )}
        </div>
      </main>

      {/* New Document Modal */}
      {showNewModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-[var(--v-surface-1)] rounded-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-[#2D3748] mb-6">Subir Nuevo Documento</h3>
            <form className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-[#2D3748] mb-2">Título</label>
                <input type="text" className="w-full px-4 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl focus:border-[#36CCD8] focus:outline-none" placeholder="Nombre del documento" />
              </div>
              <div>
                <label className="block text-sm font-bold text-[#2D3748] mb-2">Categoría</label>
                <select className="w-full px-4 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl focus:border-[#36CCD8] focus:outline-none">
                  <option value="reglamento">Reglamento</option>
                  <option value="acta">Acta</option>
                  <option value="manual">Manual</option>
                  <option value="otros">Otros</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-[#2D3748] mb-2">Descripción</label>
                <textarea className="w-full px-4 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl focus:border-[#36CCD8] focus:outline-none h-24 resize-none" placeholder="Breve descripción del contenido..." />
              </div>
              <div>
                <label className="block text-sm font-bold text-[#2D3748] mb-2">Archivo PDF</label>
                <div className="border-2 border-dashed border-[var(--v-border-light)] rounded-xl p-6 text-center hover:border-[#36CCD8] transition-colors">
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Arrastra un archivo o haz clic para seleccionar</p>
                  <p className="text-xs text-gray-400 mt-1">PDF, máximo 10MB</p>
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button 
                  type="button"
                  onClick={() => setShowNewModal(false)}
                  className="flex-1 py-3 border border-[var(--v-border-light)] text-gray-700 font-bold rounded-xl hover:bg-[var(--v-surface-2)]"
                >
                  Cancelar
                </button>
                <button 
                  type="button"
                  onClick={() => { setShowNewModal(false); alert('✅ Documento subido'); }}
                  className="flex-1 py-3 bg-[#36CCD8] text-white font-bold rounded-xl hover:bg-[#2DB8C4]"
                >
                  Subir Documento
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewingRegulation && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-[var(--v-surface-1)] rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-[var(--v-border-light)] flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-[#2D3748]">{viewingRegulation.title}</h3>
                <p className="text-sm text-gray-500">{viewingRegulation.fileName} • {viewingRegulation.fileSize}</p>
              </div>
              <button onClick={() => setViewingRegulation(null)} className="p-2 hover:bg-[var(--v-surface-2)] rounded-xl">
                <X className="w-6 h-6 text-gray-400" />
              </button>
            </div>
            <div className="p-6 flex-1 overflow-y-auto">
              <div className="bg-[var(--v-surface-2)] rounded-xl p-12 text-center">
                <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">Vista previa del documento</p>
                <p className="text-sm text-gray-400">{viewingRegulation.description}</p>
              </div>
            </div>
            <div className="p-4 border-t border-[var(--v-border-light)] flex gap-3">
              <button 
                onClick={() => handleDownload(viewingRegulation)}
                className="flex-1 py-3 bg-[#36CCD8] text-white font-bold rounded-xl hover:bg-[#2DB8C4] flex items-center justify-center gap-2"
              >
                <Download className="w-5 h-5" /> Descargar PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
